import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { featuredVideos } from '@/mocks/videos';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % featuredVideos.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const video = featuredVideos[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={video.thumbnail}
          alt={video.title}
          className={`w-full h-full object-cover object-top transition-opacity duration-700 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
      </div>

      <div className="absolute inset-0 flex items-end pb-12 md:pb-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded whitespace-nowrap">
                热门推荐
              </span>
              {video.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-white/20 text-white text-xs rounded whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <h2 className={`text-2xl md:text-4xl font-bold text-white mb-3 transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {video.title}
            </h2>

            <p className={`text-sm md:text-base text-white/80 mb-4 line-clamp-2 max-w-lg transition-all duration-500 delay-100 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {video.description}
            </p>

            <div className={`flex items-center gap-4 transition-all duration-500 delay-200 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <div className="flex items-center gap-1 text-accent-500">
                <i className="ri-star-fill text-sm"></i>
                <span className="text-white font-semibold text-sm">{video.rating}</span>
              </div>
              <span className="text-white/60 text-sm">{video.year}</span>
              <span className="text-white/60 text-sm">{video.duration}</span>
              <span className="text-white/60 text-sm">{video.views}次播放</span>
            </div>

            <div className={`flex items-center gap-3 mt-5 transition-all duration-500 delay-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <Link
                to={`/article/${video.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${video.categorySlug}`}
                className="inline-flex items-center gap-1 px-4 py-2.5 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg cursor-pointer transition-colors whitespace-nowrap"
              >
                更多{video.category}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-6 flex items-center gap-2">
        {featuredVideos.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full cursor-pointer transition-all duration-300 ${
              index === current
                ? 'bg-primary-500 w-6'
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + featuredVideos.length) % featuredVideos.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-full cursor-pointer transition-colors"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-full cursor-pointer transition-colors"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </section>
  );
}